/**
 * exportTableXlsx - client-side Excel export of a batch table.
 *
 * CSV goes through the backend (`exportTable(uuid, 'csv')`); Excel is built
 * here from the detail payload the page already holds, so it needs no new
 * endpoint. `exceljs` is imported lazily: it is a large library and only the
 * person who clicks "Export Excel" should pay for it.
 */

import { ComplianceStatus, TableColumn, TableDetail, TableRow } from '../../types/batchTables';

const COMPLIANCE_LABELS: Record<ComplianceStatus, string> = {
  compliant: 'Compliant',
  non_compliant: 'Non-compliant',
  needs_review: 'Needs review',
  not_found: 'Not found',
};

export interface XlsxSheetData {
  header: string[];
  rows: string[][];
}

/**
 * The sheet as plain strings - header row first, one row per document, one
 * column per table column in display order. A compliance cell with no free-text
 * value exports its verdict, the same thing the grid shows in its place.
 */
export function buildTableSheet(table: Pick<TableDetail, 'columns' | 'rows'>, documentLabel: string): XlsxSheetData {
  const columns = (table.columns || []).slice().sort((a: TableColumn, b: TableColumn) => a.sort_order - b.sort_order);
  const rows = (table.rows || [])
    .filter((r: TableRow) => !r._placeholder)
    .slice()
    .sort((a: TableRow, b: TableRow) => a.sort_order - b.sort_order);

  return {
    header: [documentLabel, ...columns.map((c) => c.name)],
    rows: rows.map((row) => [
      row.file_name || '',
      ...columns.map((col) => {
        const cell = row.cells?.[String(col.id)];
        if (!cell) return '';
        const value = cell.value != null ? String(cell.value).trim() : '';
        if (value) return value;
        return cell.compliance_status ? COMPLIANCE_LABELS[cell.compliance_status] || '' : '';
      }),
    ]),
  };
}

/** A filename the OS accepts, from the table's name. */
export function xlsxFileName(name: string | null | undefined): string {
  const base = (name || 'table').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'table';
  return `${base.slice(0, 120)}.xlsx`;
}

export async function exportTableXlsx(table: TableDetail, documentLabel: string): Promise<void> {
  const { default: ExcelJS } = await import('exceljs');
  const { header, rows } = buildTableSheet(table, documentLabel);

  const workbook = new ExcelJS.Workbook();
  // Excel caps sheet names at 31 chars and forbids a few characters.
  const sheetName = (table.name || 'Table').replace(/[\\/?*[\]:]+/g, ' ').slice(0, 31) || 'Table';
  const sheet = workbook.addWorksheet(sheetName, { views: [{ state: 'frozen', xSplit: 1, ySplit: 1 }] });

  sheet.addRow(header);
  rows.forEach((r) => sheet.addRow(r));

  sheet.getRow(1).font = { bold: true };
  sheet.columns.forEach((col, i) => {
    col.width = i === 0 ? 40 : 32;
    col.alignment = { vertical: 'top', wrapText: true };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = xlsxFileName(table.name);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
